import React, { useState } from "react";
import { supabase } from "../supabaseClient";
import { downloadCSV } from "../utils/ExpenseHelpers";

// Received allowedProjects prop from parent context
function PersonalExpenseSection({ user, ledger, getName, refreshAll, allowedProjects = [] }) {
  const [personalExpense, setPersonalExpense] = useState({ amount: "", note: "", project_id: "" });

  const submitPersonalExpense = async () => {
    const parsedAmount = Number(personalExpense.amount);
    const normalizedNote = personalExpense.note.trim();

    // Require a project to be selected alongside valid numeric amounts and notes
    if (!personalExpense.amount || isNaN(parsedAmount) || parsedAmount <= 0 || !normalizedNote || !personalExpense.project_id) {
      alert("Please enter a valid amount greater than 0, a project selection, and a descriptive note.");
      return;
    }

    const { error } = await supabase.from("wallet_ledger").insert({
      user_id: user?.id,
      counterparty_id: user?.id,
      amount: parsedAmount,
      entry_type: "debit",
      note: normalizedNote || "Personal Expenditure",
      project_id: personalExpense.project_id, // Attaching the chosen UUID parameter token
      created_at: new Date().toISOString()
    });

    if (!error) {
      setPersonalExpense({ amount: "", note: "", project_id: "" });
      refreshAll();
    } else {
      console.error("Error adding expense:", error.message);
      alert(`Failed to save expense: ${error.message}`);
    }
  };

  // Helper dictionary lookup used locally to stitch CSV records cleanly
  const getProjectInfo = (projId) => {
    if (!projId) return { name: "No Project", code: "-", client: "-" };
    const match = allowedProjects.find((p) => p.project_id === projId);
    return match 
      ? { 
          name: match.project_name, 
          code: match.project_code || "-", 
          client: match.project_meta?.client_name || "-" 
        } 
      : { name: "Unknown Project", code: "-", client: "-" };
  };

  const handleDownloadPersonalCSV = () => {
    const safeLedger = Array.isArray(ledger) ? ledger : [];

    const personalLedger = safeLedger
      .filter((item) => !item.transaction_id)
      .map((item) => {
        // Resolve target properties on demand using the ledger link pointer reference
        const projectDetails = getProjectInfo(item.project_id);

        return {
          Date: new Date(item.created_at).toLocaleDateString(),
          Time: new Date(item.created_at).toLocaleTimeString(),
          "Client Name": projectDetails.client,
          "Project Name": projectDetails.name,
          "Project Code": projectDetails.code,
          User: getName(item.user_id),
          Type: item.entry_type,
          Amount: item.amount,
          Note: item.note || ""
        };
      });

    downloadCSV(personalLedger, "personal_expenses.csv");
  };

  return (
    <div style={{ background: "#fff", padding: "20px", borderRadius: 12, marginBottom: 25, border: "1px solid #e5e7eb", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
      
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "#374151" }}>Record My Personal Expenditure</h3>
        <button
          onClick={handleDownloadPersonalCSV}
          style={{ background: "none", color: "#dc2626", border: "none", padding: 0, cursor: "pointer", fontWeight: "600", fontSize: "13px", textDecoration: "underline" }}
        >
          Download Personal CSV
        </button>
      </div>

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {/* NEW FEATURE: PROJECT SELECTION DROPDOWN */}
        <select
          value={personalExpense.project_id}
          onChange={(e) => setPersonalExpense({ ...personalExpense, project_id: e.target.value })}
          style={{ flex: 1, minWidth: "180px", padding: "10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: "14px", backgroundColor: "#fff" }}
        >
          <option value="">Select Project</option>
          {allowedProjects.map((project) => {
            const clientLabel = project.project_meta?.client_name ? `(${project.project_meta.client_name}) ` : "";
            const codeLabel = project.project_code ? `[${project.project_code}] ` : "";
            return (
              <option key={project.project_id} value={project.project_id}>
                {clientLabel}{codeLabel}{project.project_name}
              </option>
            );
          })}
        </select>

        <input 
          type="number" 
          placeholder="Amount ₹" 
          min="1"
          value={personalExpense.amount} 
          onChange={(e) => setPersonalExpense({ ...personalExpense, amount: e.target.value })}
          style={{ width: "120px", padding: "10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: "14px" }}
        />
        
        <input 
          type="text" 
          placeholder="* Enter expenditure note"  
          value={personalExpense.note} 
          onChange={(e) => setPersonalExpense({ ...personalExpense, note: e.target.value })}
          style={{ flex: 2, minWidth: "200px", padding: "10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: "14px" }}
        />
        
        <button 
          onClick={submitPersonalExpense}
          style={{ background: "#ef4444", color: "#fff", padding: "10px 20px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: "bold", fontSize: "14px" }}
        >
          Add Expense
        </button>
      </div>

    </div>
  );
}

export default PersonalExpenseSection;